import { GhostButton } from '@ardoq/button';
import { AnimatedChevron } from '@ardoq/icons';
import { useState, MouseEvent, KeyboardEvent } from 'react';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { Island, IslandProps } from './Island';

type CollapsibleIslandProps = IslandProps & {
  isDefaultExpanded?: boolean;
  onToggle?: (isExpanded: boolean) => void;
  collapsedMinHeight?: string;
  toggleOnHeaderClick?: boolean;
  i18nLocale?: I18nLocale;
};

export const CollapsibleIsland = ({
  isDefaultExpanded,
  onToggle,
  toggleOnHeaderClick = true,
  i18nLocale,
  ...islandProps
}: CollapsibleIslandProps) => {
  const [isExpanded, setIsExpanded] = useState(isDefaultExpanded ?? false);
  const toggleExpand = () => {
    onToggle?.(!isExpanded);
    setIsExpanded(!isExpanded);
  };

  const islandMinHeight = isExpanded
    ? islandProps.minHeight
    : (islandProps.collapsedMinHeight ?? islandProps.minHeight);

  return (
    <ControlledCollapsibleIsland
      {...islandProps}
      minHeight={islandMinHeight}
      isExpanded={isExpanded}
      onToggle={toggleExpand}
      toggleOnHeaderClick={toggleOnHeaderClick}
      i18nLocale={i18nLocale}
    />
  );
};

type ControlledCollapsibleIslandProps = IslandProps & {
  isExpanded: boolean;
  onToggle: (isExpanded: boolean) => void;
  toggleOnHeaderClick?: boolean;
  i18nLocale?: I18nLocale;
};

export const ControlledCollapsibleIsland = ({
  isExpanded,
  onToggle,
  children,
  rightContent,
  toggleOnHeaderClick = true,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  ...islandProps
}: ControlledCollapsibleIslandProps) => {
  const toggleExpand = (event: MouseEvent | KeyboardEvent) => {
    event.stopPropagation();
    event.preventDefault();
    onToggle?.(!isExpanded);
  };

  const toggleLabel = isExpanded
    ? t(i18nLocale, 'pageLayout:collapsibleIsland.collapse', 'Collapse')
    : t(i18nLocale, 'pageLayout:collapsibleIsland.expand', 'Expand');

  return (
    <Island
      {...islandProps}
      i18nLocale={i18nLocale}
      onHeaderClicked={toggleOnHeaderClick ? toggleExpand : undefined}
      rightContent={
        <>
          {rightContent}
          <GhostButton
            onClick={toggleExpand}
            aria-label={toggleLabel}
            data-tooltip-text={toggleLabel}
          >
            <AnimatedChevron $isUp={isExpanded} />
          </GhostButton>
        </>
      }
    >
      {isExpanded && children}
    </Island>
  );
};
